<template>
  <div>
    <div class="panel-header">
      <div>
        <div class="panel-title">用户特殊数据权限</div>
        <div class="panel-desc">
          按人授权的固定目标值权限（跨部门、指定业务ID等），与岗位无关，运行时与岗位默认权限合并（取并集）
        </div>
      </div>
      <a-space>
        <user-select v-model="queryUserId" @select="onQueryUserSelect" />
        <a-button @click="query">查询</a-button>
        <a-button type="primary" icon="plus" @click="openGrant">授予权限</a-button>
      </a-space>
    </div>

    <a-table
      bordered
      rowKey="id"
      size="middle"
      :loading="loading"
      :columns="columns"
      :dataSource="list"
      :pagination="{
        total,
        current,
        pageSize,
        showSizeChanger: true,
        showTotal: (total) => `共 ${total} 条记录`,
      }"
      @change="onTableChange"
    >
      <template slot="userCol" slot-scope="text, record">
        {{ record.userName || "--" }}<span v-if="record.userAccount">（{{ record.userAccount }}）</span>
      </template>
      <template slot="codeCol" slot-scope="text, record">
        {{ record.code }}<span v-if="record.codeName">（{{ record.codeName }}）</span>
      </template>
      <template slot="expireCol" slot-scope="text, record">
        <template v-if="!text">
          <a-tag color="green">永久</a-tag>
        </template>
        <template v-else>
          <a-tag :color="isExpired(record) ? 'red' : 'blue'">{{ isExpired(record) ? "已过期" : text }}</a-tag>
        </template>
      </template>
      <template slot="actionCol" slot-scope="text, record">
        <a class="danger-link" @click="revoke(record)">回收</a>
      </template>
    </a-table>

    <!-- 授予权限 -->
    <a-modal
      title="授予用户特殊数据权限"
      :visible="grantVisible"
      :confirm-loading="grantLoading"
      width="560px"
      @ok="submitGrant"
      @cancel="grantVisible = false"
    >
      <a-form-model
        ref="grantForm"
        :model="grantForm"
        :rules="grantRules"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 17 }"
      >
        <a-form-model-item label="用户" prop="userId">
          <user-select v-model="grantForm.userId" placeholder="请选择用户" />
        </a-form-model-item>
        <a-form-model-item label="权限编码" prop="code">
          <a-select
            v-model="grantForm.code"
            show-search
            allow-clear
            option-filter-prop="children"
            placeholder="选择数据权限定义"
          >
            <a-select-option v-for="item in dpList" :key="item.code" :value="item.code">
              {{ item.code }}（{{ item.name }}）
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="权限值" prop="value">
          <a-textarea
            v-model="grantForm.value"
            :rows="2"
            :placeholder="valuePlaceholder"
          />
        </a-form-model-item>
        <a-form-model-item label="失效时间">
          <a-date-picker
            v-model="grantForm.expireAt"
            style="width: 100%"
            placeholder="不选=永久（重复授予同编码为续期/改值）"
            value-format="YYYY-MM-DD"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import {
  fetchUserDataPerms,
  grantUserDataPerm,
  revokeUserDataPerm,
  fetchDataPermissionList,
} from "@/api/setting/auth";
import UserSelect from "./user-select";

export default {
  name: "user-data-permission",

  components: { UserSelect },

  data() {
    return {
      loading: false,
      queryUserId: undefined,
      queryUser: null,
      list: [],
      // 后端分页参数与总数
      current: 1,
      pageSize: 10,
      total: 0,
      dpList: [],
      columns: [
        { title: "用户", dataIndex: "userName", width: 150, scopedSlots: { customRender: "userCol" } },
        { title: "部门", dataIndex: "deptName", width: 150 },
        { title: "权限", dataIndex: "code", width: 260, scopedSlots: { customRender: "codeCol" } },
        { title: "业务表", dataIndex: "tableName", width: 180 },
        { title: "权限值", dataIndex: "value", ellipsis: true },
        { title: "失效时间", dataIndex: "expireAt", width: 130, align: "center", scopedSlots: { customRender: "expireCol" } },
        { title: "操作", width: 70, align: "center", scopedSlots: { customRender: "actionCol" } },
      ],
      grantVisible: false,
      grantLoading: false,
      grantForm: {},
      grantRules: {
        userId: [{ required: true, message: "请选择用户" }],
        code: [{ required: true, message: "请选择权限编码" }],
        value: [{ required: true, message: "请输入权限值" }],
      },
    };
  },

  computed: {
    valuePlaceholder() {
      const item = this.dpList.find((d) => d.code === this.grantForm.code);
      if (!item) {
        return "固定目标值，支持逗号分隔多值（如 HT0001,HT0002）";
      }
      if (item.type === "url") {
        return `按「${item.url || "取值接口"}」的${item.urlField || item.tableField}填写，支持逗号分隔多值`;
      }
      if (item.tableField === "*") {
        return "该定义为全表权限，值填 * 即可";
      }
      return `按业务表「${item.tableName}」字段「${item.tableField}」填写，支持逗号分隔多值`;
    },
  },

  mounted() {
    this.load();
    this.loadDefines();
  },

  methods: {
    isExpired(record) {
      return record.expireAt && new Date(record.expireAt.replace(/-/g, "/")).getTime() < Date.now();
    },
    onQueryUserSelect(user) {
      this.queryUser = user;
    },
    // 查询按钮：重置到第一页再查
    query() {
      this.current = 1;
      this.load();
    },
    // 翻页/改每页条数
    onTableChange(page) {
      this.current = page.current;
      this.pageSize = page.pageSize;
      this.load();
    },
    loadDefines() {
      fetchDataPermissionList()
        .then((res) => {
          this.dpList = Array.isArray(res) ? res : [];
        })
        .catch(() => {});
    },
    load() {
      this.loading = true;
      fetchUserDataPerms({
        userId: this.queryUserId,
        pageNum: this.current,
        pageSize: this.pageSize,
      })
        .then((res) => {
          this.loading = false;
          // 后端返回DataVo：{ list, totalSize, pageNum, pageSize, totalPages }
          this.list = (res && res.list) || [];
          this.total = (res && res.totalSize) || 0;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openGrant() {
      this.grantForm = {
        // 预填当前查询的用户
        userId: this.queryUserId,
        code: undefined,
        value: undefined,
        expireAt: null,
      };
      this.grantVisible = true;
    },
    submitGrant() {
      this.$refs.grantForm.validate((valid) => {
        if (!valid) {
          return;
        }
        this.grantLoading = true;
        grantUserDataPerm({
          userId: this.grantForm.userId,
          code: this.grantForm.code,
          value: this.grantForm.value,
          expireAt: this.grantForm.expireAt || null,
        })
          .then(() => {
            this.grantLoading = false;
            this.grantVisible = false;
            this.$message.success("授予成功");
            this.load();
          })
          .catch(() => {
            this.grantLoading = false;
          });
      });
    },
    revoke(record) {
      const that = this;
      this.$confirm({
        title: `确认回收用户「${record.userName || record.userId}」的权限「${record.code}」吗？`,
        onOk() {
          return revokeUserDataPerm(record.id).then(() => {
            that.$message.success("回收成功");
            that.load();
          });
        },
      });
    },
  },
};
</script>

<style scoped>
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.panel-desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.danger-link {
  color: #f5222d;
}
</style>
